import { useState } from "react";
import { Clipboard, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { copyToClipboard, normalizeCopyText } from "@/lib/copy";

interface CopyButtonProps {
  getText: () => string;
  label?: string;
  size?: "sm" | "icon";
  variant?: "outline" | "ghost";
}

const CopyButton = ({ getText, label = "Copy", size = "sm", variant = "outline" }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleClick = async () => {
    const raw = getText();
    const text = normalizeCopyText(raw);
    if (!text) {
      toast({ title: "Nothing to copy" });
      return;
    }
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      toast({ title: "Copied" });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast({ title: "Copy failed — select and copy manually", variant: "destructive" });
    }
  };

  if (size === "icon") {
    return (
      <Button variant={variant} size="icon" className="h-7 w-7 shrink-0" onClick={handleClick}>
        {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Clipboard className="h-3.5 w-3.5" />}
      </Button>
    );
  }

  return (
    <Button variant={variant} size="sm" className="h-7 px-2 text-xs shrink-0 text-slate-200 border-white/15" onClick={handleClick}>
      {copied ? <Check className="mr-1 h-3 w-3 text-primary" /> : <Clipboard className="mr-1 h-3 w-3" />}
      {label}
    </Button>
  );
};

export default CopyButton;
